import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Heading, HStack, Text } from '@chakra-ui/react';
import { getEarningsReport } from '../../api';
import { formatEarningsValue } from './earningsFormatting';
import EarningsComparison from './EarningsComparison';
import { hasQuarterComparison, comparisonMetrics, addComparisonMetric } from './earningsComparisonModel';
export { hasQuarterComparison } from './earningsComparisonModel';
export { MetricComparison } from './EarningsComparison';

function DocumentLink({ label, url }) {
  return url ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>{label} ↗</a>
    : <Text color="fg.muted">{label}: unavailable or not yet filed</Text>;
}

export default function StockEarningsReport({ embedded = false }) {
  const { ticker } = useParams();
  const [requestState, setRequestState] = useState({ loading: true });
  const [retry, setRetry] = useState(0);
  const [format, setFormat] = useState('full');
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setRequestState({ loading: true });
    setSelected([]);
    setMessage('');
    getEarningsReport({ ticker, signal: controller.signal }).then(data => {
      if (!controller.signal.aborted) setRequestState({ data });
    }).catch(error => {
      if (!controller.signal.aborted) setRequestState({ error: error.message });
    });
    return () => controller.abort();
  }, [ticker, retry]);
  async function copy(value, unit) {
    const text = formatEarningsValue(value, format, unit);
    try {
      await navigator.clipboard.writeText(text);
      setMessage(`Copied ${text}`);
    } catch {
      setMessage(`Clipboard unavailable. Copy this value manually: ${text}`);
    }
  }
  function addMetric(metric) {
    setSelected(previous => addComparisonMetric(previous, metric));
    document.getElementById('earnings-comparison')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }
  const { data, loading, error } = requestState;
  const metrics = comparisonMetrics(data);
  return <Box p={embedded ? 0 : { base: 3, md: 6 }} maxW="1400px" mx="auto">
    {!embedded && <Text color="fg.muted" fontSize="sm">{ticker?.toUpperCase()} / Earnings reports</Text>}
    <Heading mt={2} mb={4}>Most recent earnings</Heading>
    {loading && <Text role="status">Loading earnings report…</Text>}
    {error && <Box role="alert"><Text>{error}</Text><Button mt={3} onClick={() => setRetry(v => v + 1)}>Retry</Button></Box>}
    {data?.status === 'unavailable' && <Text>{data.message}</Text>}
    {data?.pending_release && <Box borderWidth="1px" borderColor="blue.400" borderRadius="lg" p={4} mb={6}>
      <Heading fontSize="22px" lineHeight="1.35">New earnings release: period ended {data.pending_release.period_end}</Heading>
      <Text my={2}>The corresponding 10-Q or year-end 10-K is not yet available. Statements and quarterly comparisons will appear after the filing is available. The older filed statements below are labeled separately.</Text>
      <HStack gap={5} flexWrap="wrap"><DocumentLink label="Earnings press release" url={data.pending_release.press_release} /><DocumentLink label="Earnings presentation" url={data.pending_release.presentation} /><DocumentLink label="Associated 8-K" url={data.pending_release.source_8k} /></HStack>
    </Box>}
    {data?.period_end && <>
      <Heading fontSize="26px" lineHeight="1.35">{data.pending_release ? 'Latest filed statements: ' : ''}{data.period_label}</Heading>
      <Text my={2}>Period ended {data.period_end} · Filed {data.filed}</Text>
      <HStack gap={5} flexWrap="wrap" my={4}>
        <DocumentLink label="Earnings press release" url={data.documents.press_release} />
        <DocumentLink label="Earnings presentation" url={data.documents.presentation} />
        <DocumentLink label={`Complete ${data.documents.filing_type}`} url={data.documents.filing} />
        {data.documents.source_8k && <DocumentLink label="Associated 8-K" url={data.documents.source_8k} />}
      </HStack>
      {data.documents.filing_type === '10-K' && <Text fontSize="sm" mb={3}>Year-end report: the 10-K replaces the 10-Q. Tables retain annual periods; graphs show Q4 where a standalone quarter can be reported or derived.</Text>}
      {data.warnings?.map((warning, i) => <Text key={i} role="status" mb={2}>{warning}</Text>)}
      <Box borderWidth="1px" borderRadius="lg" p={4} my={5}>
        <label htmlFor="earnings-copy-format">Copy number format </label>
        <select id="earnings-copy-format" value={format} onChange={e => setFormat(e.target.value)} style={{ padding: 8, color: 'inherit', background: 'transparent', border: '1px solid #718096', borderRadius: 6 }}>
          <option value="full">Full number — $10,000,000</option>
          <option value="abbreviated">Abbreviated — $10M</option>
          <option value="words">With words — $10 million</option>
        </select>
        <Text fontSize="sm" mt={2}>Click a number to copy it. Copied values expand the statement’s thousands or millions into the selected format. Tables retain the filing’s original periods and scale.</Text>
        <Text role="status" mt={2}>{message}</Text>
      </Box>
      {metrics.length > 0 && <EarningsComparison metrics={metrics} series={selected} onChange={setSelected} ticker={ticker} />}
      {data.statements.map((statement, s) => <Box key={s} mb={8}>
        <Heading fontSize="22px" lineHeight="1.35" mb={2}>{{ income: 'Income statement', balance_sheet: 'Balance sheet', cash_flow: 'Cash flow statement' }[statement.kind] || statement.title}</Heading>
        <DocumentLink label="Source statement" url={statement.source_url} />
        <Box overflowX="auto" borderWidth="1px" borderRadius="lg" mt={3}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <caption style={{ textAlign: 'left', padding: 12 }}>As filed. Use “Compare quarters” for comparable quarterly values.</caption>
            <tbody>{statement.rows.map((row, r) => <tr key={r} style={{ borderBottom: '1px solid #71809640' }}>
              {row.cells.map((cell, c) => {
                const Tag = cell.header ? 'th' : 'td';
                return <Tag key={c} colSpan={cell.colspan} rowSpan={cell.rowspan} style={{ padding: '10px 12px', textAlign: c ? 'right' : 'left', minWidth: c ? 100 : 240 }}>
                  {cell.value != null ? <button type="button" title={`Copy ${formatEarningsValue(cell.value, format, row.unit)}`} aria-label={`Copy ${row.label}: ${formatEarningsValue(cell.value, format, row.unit)}`} onClick={() => copy(cell.value, row.unit)} style={{ cursor: 'copy', font: 'inherit', color: 'inherit', background: 'transparent', border: 0, textDecoration: 'underline', textDecorationStyle: 'dotted' }}>{cell.text}</button> : cell.text}
                  {c === 0 && hasQuarterComparison(row.comparisons) && <Button display="block" size="xs" variant="ghost" mt={1} onClick={() => addMetric(metrics.find(metric => metric.id === `${s}:${r}`))} disabled={selected.some(metric => metric.id === `${s}:${r}`) || addComparisonMetric(selected, metrics.find(metric => metric.id === `${s}:${r}`)) === selected}>{selected.some(metric => metric.id === `${s}:${r}`) ? 'Added to comparison' : 'Compare quarters'}</Button>}
                </Tag>;
              })}
            </tr>)}</tbody>
          </table>
        </Box>
      </Box>)}
    </>}
  </Box>;
}
